import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import {
  CustomInput,
  CustomNumberInput,
  CustomTextarea,
  CustomToggle,
} from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import { useOrganizationApi } from '../Services/OrganizationApiProvider';

function OrganizationDetailContent() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { getOrganizationById, updateOrganization } = useOrganizationApi();

  const [loading, setLoading] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [activeCommunications, setActiveCommunications] = useState(0);
  const [communicationsPerDay, setCommunicationsPerDay] = useState(0);
  const [communicationsPerMonth, setCommunicationsPerMonth] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getOrganizationById(organizationId, true);

        if (data) {
          setName(data.title || data.name || '');
          setDescription(data.description || '');
          setIsActive(data.is_active ?? true);
          setActiveCommunications(data.active_communications || 0);
          setCommunicationsPerDay(data.communications_per_day || 0);
          setCommunicationsPerMonth(data.communications_per_month || 0);
        } else {
          navigate('/404');
        }
      } catch (error) {
        console.error('Error fetching organization:', error);
        showAlert('fail', 'Failed to load organization data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId, navigate]);

  const onCancel = () => {
    navigate(`/organizations/${organizationId}`);
  };

  const onSave = async () => {
    const data = {
      description: description,
      is_active: isActive,
      active_communications: parseInt(activeCommunications),
      communications_per_day: parseInt(communicationsPerDay),
      communications_per_month: parseInt(communicationsPerMonth),
    };

    try {
      setLoading(true);
      await updateOrganization(organizationId, data);

      navigate(`/organizations/${organizationId}`, {
        state: {
          successMessage: 'Organization updated successfully',
        },
      });
    } catch (error) {
      console.error('Error updating organization:', error);
      showAlert('fail', 'Failed to update organization');
      setLoading(false);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row">
        <div className="col-6 radius-yes p-2 bg-yes">
          <div className="d-flex flex-row justify-between align-center">
            <h2 className="h2-title">{`Edit Organization: ${name}`}</h2>
            <Button
              background="color2"
              size="medium"
              onClick={() => navigate(`/organizations/${organizationId}/ai-keys`)}
            >
              AI Keys
            </Button>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Name</div>
              <div className="small-font">Organization Name</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomInput id="org-name" label="Name" type="text" value={name} disabled={true} />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Description</div>
              <div className="small-font">Enter organization description</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomTextarea
                id="description"
                addedText={description}
                onChange={(value) => setDescription(value)}
                resizable={false}
                disabled={isDisabled}
                height="150px"
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Active Communications</div>
              <div className="small-font">Set active communications</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomNumberInput
                id="active-communications"
                value={activeCommunications}
                onChange={(value) => setActiveCommunications(value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Communications Per Day</div>
              <div className="small-font">Set communications per day</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomNumberInput
                id="comms-per-day"
                value={communicationsPerDay}
                onChange={(value) => setCommunicationsPerDay(value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Communications Per Month</div>
              <div className="small-font">Set communications per month</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomNumberInput
                id="comms-per-month"
                value={communicationsPerMonth}
                onChange={(value) => setCommunicationsPerMonth(value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-row input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Organization Status</div>
              <div className="small-font">Enable or disable this organization</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomToggle
                id="toggle-status"
                label={isActive ? 'Active' : 'Inactive'}
                checked={isActive}
                onChange={(id, value) => setIsActive(value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="buttons-container justify-end gap-12">
            <Button background="color2" size="medium" disabled={isDisabled} onClick={onSave}>
              Save
            </Button>
            <Button variant="danger" size="medium" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrganizationDetail() {
  return (
    <ConfirmationProvider>
      <OrganizationDetailContent />
    </ConfirmationProvider>
  );
}

export default OrganizationDetail;
